import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { onboardingAPI } from '../../api/onboarding';
import { stepNumberOf } from '../../lib/tourEngine';
import { TOUR_STEPS } from '../../lib/tourSteps';
import { authAPI } from '../../api/client';
import UserAvatar from '../../components/UserAvatar';
import SandboxTools from '../../components/SandboxTools';
import { Settings, HelpCircle, Users, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Trash2, X, Eye, EyeOff, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  lider_proyecto: 'Líder',
  levantador: 'Levantador',
  cliente: 'Cliente',
};

function TourToggle({ enabled, onChange, saving }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      disabled={saving}
      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-60 ${enabled ? 'bg-soem-primary' : 'bg-slate-300'}`}
      data-testid="tour-toggle"
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${enabled ? 'translate-x-8' : 'translate-x-1'}`} />
    </button>
  );
}

function OnboardingStatsSection() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    return Promise.all([
      onboardingAPI.adminStats().catch(() => ({ data: null })),
      import('../../api/client').then(m => m.usersAPI.list().catch(() => ({ data: { items: [] } }))),
    ]).then(([statsRes, usersRes]) => {
      setStats(statsRes.data);
      setUsers(usersRes.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReset = async (userId, userName) => {
    if (!window.confirm(`¿Reiniciar el tour de ${userName}?`)) return;
    try {
      // #ResetTourUsuarioCorrecto — antes llamaba onboardingAPI.reset(),
      // que pega a /users/me/onboarding/reset: reiniciaba el tour del
      // ADMIN que hacía clic, no el del usuario de la fila. El userId ya
      // llegaba como parámetro pero se ignoraba, así que el estado del
      // usuario objetivo nunca cambiaba — de ahí que "no se reiniciara".
      // El backend ya exponía PATCH /users/{user_id}/onboarding/reset.
      await onboardingAPI.resetUser(userId);
      toast.success(`Tour de ${userName} reiniciado`);
      // Refrescar para que la fila y los contadores reflejen el cambio;
      // antes sólo salía el toast y la tabla se quedaba igual.
      await load();
    } catch { toast.error('Error al reiniciar'); }
  };

  // El backend devuelve cada métrica como {count, percentage} en lugar
  // de un número plano. Antes el componente intentaba renderizar el
  // objeto entero y React tronaba con el error #31 (objects are not
  // valid as a React child) — ahora extraemos `.count`/`.percentage`
  // (#42OcWDnO).
  const metricCount = (m) => (typeof m === 'object' && m !== null ? (m.count || 0) : (m || 0));
  const completedCount = metricCount(stats?.completed);
  const inProgressCount = metricCount(stats?.in_progress);
  const skippedCount = metricCount(stats?.skipped);
  // Backend usa `never_started` (no `not_started`).
  const notStartedCount = metricCount(stats?.never_started ?? stats?.not_started);

  if (!expanded) {
    return (
      <button onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-between p-4 soem-card hover:bg-slate-50 transition-colors"
        data-testid="onboarding-stats-toggle">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-soem-primary" />
          <span className="font-semibold text-soem-navy text-sm">Progreso de onboarding del equipo</span>
          {stats && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-medium">
              {completedCount}/{stats.total_users || 0} completados
            </span>
          )}
        </div>
        <ChevronDown size={16} className="text-slate-400" />
      </button>
    );
  }

  const pct = stats?.total_users > 0 ? Math.round((completedCount / stats.total_users) * 100) : 0;

  return (
    <div className="soem-card overflow-hidden">
      <button onClick={() => setExpanded(false)}
        className="w-full flex items-center justify-between p-4 border-b border-slate-100 hover:bg-slate-50">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-soem-primary" />
          <span className="font-bold text-soem-navy text-sm">Progreso de onboarding del equipo</span>
        </div>
        <ChevronUp size={16} className="text-slate-400" />
      </button>

      {/* Mini dashboard */}
      {stats && (
        <div className="p-4 border-b border-slate-100">
          <div className="grid grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Completado', value: completedCount, color: '#15803D' },
              { label: 'En progreso', value: inProgressCount, color: '#D97706' },
              { label: 'Saltado', value: skippedCount, color: '#94a3b8' },
              { label: 'Sin iniciar', value: notStartedCount, color: '#e2e8f0' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-black" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-slate-100 rounded-full h-2">
              <div className="h-2 bg-soem-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-bold text-soem-navy">{pct}%</span>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center"><div className="animate-spin rounded-full h-6 w-6 border-4 border-soem-primary border-t-transparent mx-auto" /></div>
        ) : (
          users.map(u => {
            const record = stats?.records?.find(r => r.user_id === u.id);
            const avatarUser = { id: u.id, name: u.name, first_name: u.first_name, last_name: u.last_name };
            let statusLabel = '○ Sin iniciar';
            let statusColor = 'text-slate-400';
            if (record?.tour_completed) { statusLabel = '✅ Completado'; statusColor = 'text-green-600'; }
            else if (record?.tour_skipped) { statusLabel = '⏭ Saltado'; statusColor = 'text-slate-500'; }
            // La API guarda el id del último paso completado, no un índice: el número se calcula aquí.
            else if (record?.current_step) { statusLabel = `🔄 Paso ${stepNumberOf(u.role, record.current_step)} de ${TOUR_STEPS[u.role]?.length || 0}`; statusColor = 'text-orange-600'; }

            return (
              <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                <UserAvatar user={avatarUser} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-soem-navy truncate">{u.name}</p>
                  <p className="text-xs text-slate-400">{ROLE_LABELS[u.role]}</p>
                </div>
                <span className={`text-xs font-semibold ${statusColor} flex-shrink-0`}>{statusLabel}</span>
                <button onClick={() => handleReset(u.id, u.name)}
                  className="text-slate-300 hover:text-soem-primary flex-shrink-0" title="Reiniciar tour">
                  <RefreshCw size={13} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Danger Zone Components ────────────────────────────────────────────────────
function DangerZoneModal({ isOpen, onClose, user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const CONFIRM_TEXT = 'ELIMINAR TODO';

  const itemsToDelete = [
    'Todos los clientes registrados',
    'Todos los proyectos y evaluaciones',
    'Todas las locaciones y eventos',
    'Todo el personal y sus asignaciones',
    'Todos los servicios de emergencia y autoridades',
    'Todo el registro de auditoría',
  ];

  const handleClose = () => {
    if (deleting) return; // Prevent closing during deletion
    setStep(1);
    setPassword('');
    setConfirmText('');
    setPasswordError('');
    setDeleteResult(null);
    setDeleteError(null);
    onClose();
  };

  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      setPasswordError('Ingresa tu contraseña');
      return;
    }
    setVerifying(true);
    setPasswordError('');
    try {
      await authAPI.login(user?.email, password);
      setStep(3);
    } catch {
      setPasswordError('Contraseña incorrecta');
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== CONFIRM_TEXT) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const token = localStorage.getItem('soem_access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/reset-all-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ confirmation: CONFIRM_TEXT }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Error al eliminar datos');
      }
      setDeleteResult(data);
      // Reload the page to clear all caches and start fresh
      // This is more reliable than trying to clear React Query cache
    } catch (err) {
      setDeleteError(err.message || 'Error al eliminar datos');
    } finally {
      setDeleting(false);
    }
  };

  const handleGoToDashboard = () => {
    // Do a full page reload to clear all caches and data
    window.location.href = '/dashboard';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60" 
        onClick={deleting || deleteResult ? undefined : handleClose} 
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-sm shadow-2xl w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        {/* Close button - hidden during deletion */}
        {!deleting && !deleteResult && (
          <button aria-label="Cerrar" 
            onClick={handleClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          >
            <X size={20} />
          </button>
        )}

        {/* Step 1: Warning */}
        {step === 1 && !deleteResult && !deleteError && (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-soem-navy">¿Estás absolutamente seguro?</h2>
              <p className="text-slate-500 text-sm mt-2">
                Esta acción eliminará de forma permanente e irreversible <strong>TODOS</strong> los registros del sistema. 
                No existe forma de recuperar los datos una vez eliminados.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-6">
              <p className="text-xs font-bold text-slate-600 uppercase mb-2">Lo que se eliminará:</p>
              <ul className="space-y-1">
                {itemsToDelete.map((item, i) => (
                  <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handleClose}
                className="flex-1 soem-btn-secondary py-2.5 text-sm"
              >
                Cancelar
              </button>
              <button 
                onClick={() => setStep(2)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-sm text-sm transition-colors"
              >
                Entiendo, continuar →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Password verification */}
        {step === 2 && !deleteResult && !deleteError && (
          <div className="p-6">
            <h2 className="text-xl font-bold text-soem-navy mb-2">Confirma tu identidad</h2>
            <p className="text-slate-500 text-sm mb-6">
              Por seguridad, ingresa tu contraseña de administrador para continuar.
            </p>

            <div className="mb-4">
              <label htmlFor="f-contraseña" className="soem-label block mb-1.5">Contraseña</label>
              <div className="relative">
                <input
id="f-contraseña"                   type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                  className={`w-full px-3 py-2.5 border rounded-sm pr-10 focus:outline-none focus:ring-2 focus:ring-soem-primary ${passwordError ? 'border-red-400' : 'border-slate-300'}`}
                  placeholder="Ingresa tu contraseña"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordError && (
                <p className="text-red-500 text-xs mt-1.5">{passwordError}</p>
              )}
            </div>

            <p className="text-xs text-slate-400 mb-6">
              Esta acción quedará registrada con tu nombre y la fecha y hora exacta.
            </p>

            <div className="flex gap-3">
              <button 
                onClick={() => setStep(1)}
                className="flex-1 soem-btn-secondary py-2.5 text-sm"
              >
                ← Atrás
              </button>
              <button 
                onClick={handleVerifyPassword}
                disabled={verifying || !password.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-sm text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {verifying ? <Loader2 size={16} className="animate-spin" /> : null}
                {verifying ? 'Verificando...' : 'Verificar y continuar →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation text */}
        {step === 3 && !deleting && !deleteResult && !deleteError && (
          <div className="p-6">
            <h2 className="text-xl font-bold text-soem-navy mb-2">Escribe el texto de confirmación</h2>
            <p className="text-slate-500 text-sm mb-4">
              Para confirmar, escribe exactamente:
            </p>

            <div className="bg-red-50 border-2 border-red-200 rounded-sm p-4 mb-4 text-center">
              <p className="text-red-700 font-mono font-bold text-lg tracking-wider">{CONFIRM_TEXT}</p>
            </div>

            <div className="mb-6">
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-center"
                placeholder="Escribe aquí..."
                data-testid="confirm-text-input"
              />
              {confirmText && confirmText !== CONFIRM_TEXT && (
                <p className="text-red-500 text-xs mt-1.5 text-center">El texto no coincide</p>
              )}
              {confirmText === CONFIRM_TEXT && (
                <p className="text-green-600 text-xs mt-1.5 text-center flex items-center justify-center gap-1">
                  <Check size={12} /> Texto correcto
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => { setStep(2); setConfirmText(''); }}
                className="flex-1 soem-btn-secondary py-2.5 text-sm"
              >
                ← Atrás
              </button>
              <button aria-label="Eliminar" 
                onClick={handleDelete}
                disabled={confirmText !== CONFIRM_TEXT}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-sm text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="final-delete-btn"
              >
                <Trash2 size={16} /> Eliminar todos los datos
              </button>
            </div>
          </div>
        )}

        {/* Deleting state */}
        {deleting && (
          <div className="p-10 text-center">
            <Loader2 size={48} className="text-red-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-soem-navy mb-2">Eliminando todos los registros...</h2>
            <p className="text-slate-500 text-sm">Por favor no cierres esta ventana.</p>
          </div>
        )}

        {/* Success result */}
        {deleteResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-soem-navy">Todos los datos han sido eliminados</h2>
            </div>

            {deleteResult.summary && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm overflow-hidden mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="px-4 py-2 text-left font-semibold text-slate-600">Módulo</th>
                      <th className="px-4 py-2 text-right font-semibold text-slate-600">Registros eliminados</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {Object.entries(deleteResult.summary).map(([key, val]) => (
                      <tr key={key} className={key === 'total' ? 'bg-slate-50 font-bold' : ''}>
                        <td className="px-4 py-2 text-slate-700 capitalize">{key}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-slate-400 text-center mb-6">
              Ejecutado el {new Date().toLocaleString('es-MX')} por {user?.name || 'Administrador'}
            </p>

            <button 
              onClick={handleGoToDashboard}
              className="w-full soem-btn-primary py-2.5 text-sm"
            >
              Ir al Dashboard →
            </button>
          </div>
        )}

        {/* Error result */}
        {deleteError && !deleteResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-soem-navy mb-2">Ocurrió un error al eliminar los datos</h2>
              <p className="text-red-600 text-sm">{deleteError}</p>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handleClose}
                className="flex-1 soem-btn-secondary py-2.5 text-sm"
              >
                Cancelar
              </button>
              <button 
                onClick={() => { setDeleteError(null); setStep(3); }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-sm text-sm transition-colors"
              >
                Intentar de nuevo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DangerZoneSection({ user }) {
  const [showModal, setShowModal] = useState(false);

  const deletedItems = [
    { label: 'Clientes', icon: '✗' },
    { label: 'Proyectos', icon: '✗' },
    { label: 'Evaluaciones', icon: '✗' },
    { label: 'Locaciones', icon: '✗' },
    { label: 'Eventos', icon: '✗' },
    { label: 'Personal y asignaciones', icon: '✗' },
    { label: 'Servicios de emergencia', icon: '✗' },
    { label: 'Autoridades vinculadas', icon: '✗' },
    { label: 'Registro de auditoría', icon: '✗' },
  ];

  const preservedItems = [
    { label: 'Usuario administrador principal', icon: '✓' },
    { label: 'Catálogo de equipo', icon: '✓' },
    { label: 'Catálogo del checklist', icon: '✓' },
  ];

  return (
    <>
      <div 
        className="rounded-sm border-2 border-red-300 bg-red-50 overflow-hidden"
        data-testid="danger-zone"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-red-200 bg-red-50">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            <h2 className="font-bold text-red-700 text-lg">Zona de Peligro</h2>
          </div>
          <p className="text-red-600 text-xs mt-1">
            Estas acciones son irreversibles. Procede con extrema cautela.
          </p>
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left column - Description */}
            <div className="flex-1">
              <h3 className="font-bold text-soem-navy mb-2">Eliminar todos los registros</h3>
              <p className="text-slate-600 text-sm mb-4">
                Borra permanentemente todos los clientes, proyectos, evaluaciones, locaciones, eventos, 
                personal, servicios de emergencia, autoridades y auditoría. El usuario administrador 
                principal y la configuración del sistema se conservan.
              </p>

              {/* Items that will be deleted */}
              <div className="mb-4">
                <p className="text-xs font-bold text-red-600 uppercase mb-2">Lo que SE ELIMINA:</p>
                <div className="grid grid-cols-2 gap-1">
                  {deletedItems.map((item) => (
                    <div key={item.label} className="flex items-center gap-1.5 text-red-600 text-xs">
                      <span className="font-bold">{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Items that will be preserved */}
              <div>
                <p className="text-xs font-bold text-green-600 uppercase mb-2">Lo que SE CONSERVA:</p>
                <div className="space-y-1">
                  {preservedItems.map((item) => (
                    <div key={item.label} className="flex items-center gap-1.5 text-green-600 text-xs">
                      <span className="font-bold">{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column - Button */}
            <div className="flex items-center justify-center md:justify-end">
              <button
                onClick={() => setShowModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-sm text-sm transition-colors flex items-center gap-2 shadow-lg hover:shadow-xl"
                data-testid="delete-all-data-btn"
              >
                <Trash2 size={18} />
                Eliminar todos los datos
              </button>
            </div>
          </div>
        </div>
      </div>

      <DangerZoneModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        user={user}
      />
    </>
  );
}

export default function AdminSettings() {
  const { user } = useAuth();
  const [tourEnabled, setTourEnabled] = useState(true);
  const [tourMode, setTourMode] = useState('until_completed'); // 'until_completed' or 'always_show'
  const [savingTour, setSavingTour] = useState(false);
  const [tourUpdatedAt, setTourUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      const r = await onboardingAPI.getTourConfig();
      setTourEnabled(r.data.tour_enabled);
      setTourMode(r.data.tour_mode || 'until_completed');
      setTourUpdatedAt(r.data.updated_at);
    } catch { toast.error('Error al cargar configuración'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleTourToggle = async (val) => {
    setSavingTour(true);
    try {
      const r = await onboardingAPI.setTourConfig(val, tourMode, user?.name || 'admin');
      setTourEnabled(r.data.tour_enabled);
      setTourUpdatedAt(r.data.updated_at);
      toast.success(`Tour guiado ${val ? 'activado' : 'desactivado'} para todos los usuarios`);
    } catch { toast.error('Error al guardar configuración'); }
    finally { setSavingTour(false); }
  };

  const handleModeChange = async (mode) => {
    setSavingTour(true);
    try {
      const r = await onboardingAPI.setTourConfig(tourEnabled, mode, user?.name || 'admin');
      setTourMode(mode);
      setTourUpdatedAt(r.data.updated_at);
      toast.success(`Modo del tour actualizado: ${mode === 'always_show' ? 'Mostrar siempre' : 'Hasta completar'}`);
    } catch { toast.error('Error al guardar configuración'); }
    finally { setSavingTour(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="admin-settings">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-soem-primary rounded-sm flex items-center justify-center flex-shrink-0">
          <Settings size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Configuración del sistema</h1>
          <p className="text-slate-500 text-sm mt-0.5">Ajustes globales de la plataforma SOEM</p>
        </div>
      </div>

      {/* Tour guiado setting */}
      <div className="soem-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <HelpCircle size={16} className="text-soem-primary" />
            <h2 className="font-bold text-soem-navy">Tour guiado interactivo</h2>
          </div>
          <p className="text-slate-400 text-xs mt-0.5">Controla si el tour de bienvenida se muestra automáticamente a los usuarios</p>
        </div>

        <div className="p-5 space-y-5">
          {/* Main toggle */}
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <span className={`text-base font-bold ${tourEnabled ? 'text-green-700' : 'text-slate-500'}`}>
                  {loading ? '...' : tourEnabled ? '✅ Tour activado' : '⏸ Tour desactivado'}
                </span>
                {!loading && (
                  <TourToggle enabled={tourEnabled} onChange={handleTourToggle} saving={savingTour} />
                )}
              </div>
              <p className="text-slate-500 text-sm mt-1.5">
                {tourEnabled
                  ? 'El tour de bienvenida se mostrará automáticamente a los usuarios al hacer login.'
                  : 'El tour NO se mostrará automáticamente. Los usuarios pueden iniciarlo manualmente desde el botón "?" en la esquina inferior derecha.'}
              </p>
              {tourUpdatedAt && (
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                  Última actualización: {new Date(tourUpdatedAt).toLocaleString('es-MX')}
                </p>
              )}
            </div>
          </div>

          {/* Mode selector - only show if tour is enabled */}
          {tourEnabled && !loading && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <p className="text-sm font-bold text-soem-navy mb-3">Modo de visualización del tour</p>
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-sm border-2 transition-all hover:bg-white"
                  style={{ borderColor: tourMode === 'until_completed' ? '#1A56DB' : 'transparent', backgroundColor: tourMode === 'until_completed' ? 'white' : 'transparent' }}>
                  <input type="radio" name="tour_mode" value="until_completed" checked={tourMode === 'until_completed'}
                    onChange={() => handleModeChange('until_completed')} disabled={savingTour}
                    className="mt-1 accent-soem-primary" />
                  <div>
                    <p className="font-semibold text-soem-navy text-sm">Hasta completar</p>
                    <p className="text-xs text-slate-500 mt-0.5">El tour se muestra solo a usuarios que no lo han completado ni omitido. Una vez terminado, no vuelve a aparecer automáticamente.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-sm border-2 transition-all hover:bg-white"
                  style={{ borderColor: tourMode === 'always_show' ? '#1A56DB' : 'transparent', backgroundColor: tourMode === 'always_show' ? 'white' : 'transparent' }}>
                  <input type="radio" name="tour_mode" value="always_show" checked={tourMode === 'always_show'}
                    onChange={() => handleModeChange('always_show')} disabled={savingTour}
                    className="mt-1 accent-soem-primary" />
                  <div>
                    <p className="font-semibold text-soem-navy text-sm">Mostrar siempre</p>
                    <p className="text-xs text-slate-500 mt-0.5">El tour se muestra en cada inicio de sesión mientras esté activado, sin importar si el usuario ya lo completó antes.</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Info boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-blue-50 border border-blue-100 rounded-sm p-3">
              <p className="text-xs font-bold text-blue-800 mb-1">Cuando está activado:</p>
              <ul className="text-xs text-blue-700 space-y-0.5">
                <li>• Los nuevos usuarios ven el modal de bienvenida</li>
                <li>• El tour inicia 1.5s después del login</li>
                <li>• Se puede pausar y retomar en cualquier momento</li>
              </ul>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
              <p className="text-xs font-bold text-slate-700 mb-1">Independientemente del estado:</p>
              <ul className="text-xs text-slate-500 space-y-0.5">
                <li>• El botón "?" siempre está disponible</li>
                <li>• Los usuarios pueden iniciar el tour manual</li>
                <li>• El progreso individual se conserva</li>
              </ul>
            </div>
          </div>

          {/* Steps count info */}
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
            <p className="text-xs font-bold text-slate-600 mb-2">Pasos del tour por rol:</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { role: 'Super Admin', steps: 13, emoji: '👑' },
                { role: 'Líder', steps: 8, emoji: '📋' },
                { role: 'Levantador', steps: 5, emoji: '🏟' },
                { role: 'Cliente', steps: 5, emoji: '🏢' },
              ].map(r => (
                <div key={r.role} className="text-center">
                  <span className="text-xl block mb-0.5">{r.emoji}</span>
                  <p className="text-xs font-bold text-soem-navy">{r.steps} pasos</p>
                  <p className="text-xs text-slate-400">{r.role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding stats */}
      <OnboardingStatsSection />

      {/* Sandbox Tools - Only for super_admin in non-production envs */}
      {user?.role === 'super_admin' && <SandboxTools />}

      {/* Danger Zone - Only for super_admin */}
      {user?.role === 'super_admin' && (
        <DangerZoneSection user={user} />
      )}
    </div>
  );
}
